import React, { useState } from 'react';
import { FolderTree, Layers, X } from 'lucide-react';
import { expenseAPI } from '../../services/api';
import { useToast, useConfirm } from '../UIProvider';
import Select from '../Select';
import useDialog from '../../hooks/useDialog';
import { toTitleCase } from '../../utils/text';
import { LoadingRow, errorText } from './shared';

// Two levels and no more: a department (Clinic, Construction) and the categories under it
// (Salaries, Utilities, Medicine Purchase). Cash in hand is kept per department, so every entry
// is filed against one. A category with entries is deactivated rather than deleted, so the old
// entries keep their name.

const ExpenseCategories = ({ cats, refresh }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [newDept, setNewDept] = useState('');
  const [newCat, setNewCat] = useState('');
  const [newCatParent, setNewCatParent] = useState('');
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState(null); // { id, name }
  const renameDialogRef = useDialog(() => setRenaming(null));

  const after = async () => { await cats.reload(); refresh(); };

  const addDepartment = async () => {
    if (!newDept.trim()) return;
    setBusy(true);
    try {
      await expenseAPI.createCategory({ name: newDept.trim(), parentId: null });
      showToast('success', `Department "${newDept.trim()}" added`);
      setNewDept('');
      await after();
    } catch (err) { showToast('error', errorText(err, 'Failed to add the department')); }
    finally { setBusy(false); }
  };

  const addCategory = async () => {
    if (!newCat.trim() || !newCatParent) return;
    setBusy(true);
    try {
      await expenseAPI.createCategory({ name: newCat.trim(), parentId: newCatParent });
      showToast('success', `Category "${newCat.trim()}" added`);
      setNewCat('');
      await after();
    } catch (err) { showToast('error', errorText(err, 'Failed to add the category')); }
    finally { setBusy(false); }
  };

  const toggle = async (cat) => {
    try {
      await expenseAPI.updateCategory(cat.id, { active: !(cat.active !== false) });
      showToast('success', `${cat.name} ${cat.active !== false ? 'deactivated' : 'activated'}`);
      await after();
    } catch (err) { showToast('error', errorText(err, 'Failed to update')); }
  };

  const saveRename = async () => {
    if (!renaming || !renaming.name.trim()) return;
    try {
      await expenseAPI.updateCategory(renaming.id, { name: renaming.name.trim() });
      showToast('success', 'Renamed');
      setRenaming(null);
      await after();
    } catch (err) { showToast('error', errorText(err, 'Failed to rename')); }
  };

  const remove = async (cat, isDepartment) => {
    const ok = await confirm({
      title: `Delete ${isDepartment ? 'department' : 'category'}?`,
      message: `"${cat.name}" will be removed. Only possible when nothing is filed against it${isDepartment ? ' and it has no categories' : ''}; otherwise deactivate it.`,
      confirmText: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await expenseAPI.deleteCategory(cat.id);
      showToast('success', 'Deleted');
      await after();
    } catch (err) { showToast('error', errorText(err, 'Could not delete')); }
  };

  const Actions = ({ cat, isDepartment, small }) => (
    <div className={`flex items-center gap-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity ${small ? 'text-xs' : 'text-xs'}`}>
      <button onClick={() => toggle(cat)} className={`px-2 py-0.5 rounded font-medium transition-colors ${cat.active !== false ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}`}>
        {cat.active !== false ? 'Deactivate' : 'Activate'}
      </button>
      <button onClick={() => setRenaming({ id: cat.id, name: cat.name })} className="px-2 py-0.5 rounded font-medium text-blue-600 hover:bg-blue-50 transition-colors">Rename</button>
      <button onClick={() => remove(cat, isDepartment)} className="px-2 py-0.5 rounded font-medium text-red-500 hover:bg-red-50 transition-colors">Delete</button>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl">
        <div className="p-5 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FolderTree className="h-5 w-5 text-emerald-600" /> Departments and categories
          </h3>
          <p className="text-sm text-gray-500 mt-1">Cash in hand is kept per department. Categories say what the money went on.</p>
        </div>
        {cats.loading && cats.categories.length === 0 ? (
          <LoadingRow />
        ) : cats.categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <FolderTree className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No departments yet</p>
            <p className="text-gray-400 text-sm mt-1">Add one on the right, Clinic for example</p>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {cats.categories.map(cat => (
              <div key={cat.id} className="rounded-xl border border-gray-200 overflow-hidden">
                <div className={`flex items-center justify-between px-4 py-3 group ${cat.active === false ? 'bg-gray-50' : 'bg-navy-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cat.active === false ? 'bg-gray-200' : 'bg-navy-800'}`}>
                      <Layers className={`h-4 w-4 ${cat.active === false ? 'text-gray-400' : 'text-white'}`} />
                    </div>
                    <div>
                      <span className={`font-semibold ${cat.active === false ? 'line-through text-gray-400' : 'text-gray-900'}`}>{cat.name}</span>
                      <span className="ml-2 text-xs text-gray-400">{cat.children?.length || 0} {cat.children?.length === 1 ? 'category' : 'categories'}</span>
                      {cat.active === false && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-500 rounded">inactive</span>}
                    </div>
                  </div>
                  <Actions cat={cat} isDepartment />
                </div>
                {cat.children?.length > 0 && (
                  <div className="divide-y divide-gray-100">
                    {cat.children.map(child => (
                      <div key={child.id} className="flex items-center justify-between px-4 py-2.5 pl-14 hover:bg-gray-50 group transition-colors">
                        <div className="flex items-center gap-2.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                          <span className={`text-sm ${child.active === false ? 'line-through text-gray-400' : 'text-gray-700'}`}>{child.name}</span>
                          {child.active === false && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-400 rounded">inactive</span>}
                        </div>
                        <Actions cat={child} small />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-xl p-5">
          <h4 className="font-semibold text-gray-900 mb-1">Add a department</h4>
          <p className="text-xs text-gray-500 mb-4">Whose cash it is: Clinic, Construction.</p>
          <div className="space-y-3">
            <div>
              <label htmlFor="new-department" className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
              <input
                id="new-department"
                type="text"
                value={newDept}
                onChange={(e) => setNewDept(e.target.value)}
                onBlur={(e) => setNewDept(toTitleCase(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && addDepartment()}
                placeholder="e.g. Clinic"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
              />
            </div>
            <button onClick={addDepartment} disabled={busy || !newDept.trim()} className="w-full px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium transition-colors">
              Add department
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-5">
          <h4 className="font-semibold text-gray-900 mb-1">Add a category</h4>
          <p className="text-xs text-gray-500 mb-4">What the money goes on, under one department.</p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department <span className="text-red-500">*</span></label>
              <Select
                value={newCatParent}
                onChange={setNewCatParent}
                placeholder="Pick a department"
                options={cats.roots.map(c => ({ value: c.id, label: c.name }))}
                buttonClassName="px-3 py-2 rounded-lg"
              />
            </div>
            <div>
              <label htmlFor="new-category" className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
              <input
                id="new-category"
                type="text"
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                onBlur={(e) => setNewCat(toTitleCase(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                placeholder="e.g. Salaries"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
              />
            </div>
            <button onClick={addCategory} disabled={busy || !newCat.trim() || !newCatParent} className="w-full px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium transition-colors">
              Add category
            </button>
          </div>
        </div>
      </div>

      {renaming && (
        <div ref={renameDialogRef} className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setRenaming(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="rename-title">
            <div className="px-6 py-4 bg-gradient-to-r from-navy-800 to-navy-700 flex items-center justify-between">
              <h2 id="rename-title" className="text-lg font-semibold text-white">Rename</h2>
              <button aria-label="Close" onClick={() => setRenaming(null)} className="p-2 rounded-lg hover:bg-white/10"><X className="h-5 w-5 text-white" /></button>
            </div>
            <div className="p-6">
              <label htmlFor="rename-name" className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
              <input
                id="rename-name"
                type="text"
                value={renaming.name}
                onChange={(e) => setRenaming(r => ({ ...r, name: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && saveRename()}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
              <button onClick={() => setRenaming(null)} className="px-5 py-2.5 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 font-medium">Cancel</button>
              <button onClick={saveRename} className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseCategories;
